import { defineStore } from 'pinia'

interface Department {
  id: string
  phone: string
  address: string
  map_embed: string
  map_directions: string
  trading: {
    notice: Array<{
      heading: string
      sub_heading: string
      start: string
      end: string
    }>
    [key: string]: any
  }
}

interface Slide {
  title?: string
  subtitle?: string
  image?: string
  cta_text?: string
  cta_url?: string
  background_color?: string
  published?: string
  desktop?: string
  tablet?: string
  mobile?: string
  video?: boolean
  video_poster?: string
  contrast?: string
  postion?: string
  heading_content?: string
  sub_heading?: string
  link?: string
  button_text?: string
  start?: string
  end?: string
}

export interface SiteConfig {
  manufacturer: string
  name: string
  description: string
  logo: string
  lmct: string
  showroom_address: string
  map_embed: string
  map_directions: string
  phone: string
  salesPhone?: string
  servicePartsPhone?: string
  openHours?: string
  dealerName?: string
  dealerAddress?: string
  googleRating?: string
  googleReviews?: string
  timezone: string
  departments: {
    sales: Department
    used_sales: Department
    service: Department
    parts: Department
  }
  team_members: Array<{
    title: string
    avatar: string
    department: string
    position: string
    phone: string
    mobile: string
    email: string
    content: string
  }>
  sitelinks: {
    mainnav: Array<{
      name: string
      url: string
      submenu?: {
        rendered: string
      }
    }>
    footer: Array<{
      heading: string
      links: Array<{
        title: string
        url: string
        target: string
      }>
    }>
  }
  promotional?: Array<{
    slides: Slide[]
  }>
  finance?: any
  price?: any
  payment_gateway?: string
  deposit_amount?: string
  pages?: Record<string, string>
  forms?: Record<string, string>
  _dealership_id?: string
}

export const useSiteStore = defineStore('site', {
  state: (): SiteConfig & { isLoading: boolean; error: string | null; isInitialized: boolean } => ({
    manufacturer: '',
    name: '',
    description: '',
    logo: '',
    lmct: '',
    showroom_address: '',
    map_embed: '',
    map_directions: '',
    phone: '',
    salesPhone: '',
    servicePartsPhone: '',
    openHours: '',
    dealerName: '',
    dealerAddress: '',
    googleRating: '',
    googleReviews: '',
    timezone: '',
    departments: {
      sales: {
        id: '',
        phone: '',
        address: '',
        map_embed: '',
        map_directions: '',
        trading: {
          notice: []
        }
      },
      used_sales: {
        id: '',
        phone: '',
        address: '',
        map_embed: '',
        map_directions: '',
        trading: {
          notice: []
        }
      },
      service: {
        id: '',
        phone: '',
        address: '',
        map_embed: '',
        map_directions: '',
        trading: {
          notice: []
        }
      },
      parts: {
        id: '',
        phone: '',
        address: '',
        map_embed: '',
        map_directions: '',
        trading: {
          notice: []
        }
      }
    },
    team_members: [],
    sitelinks: {
      mainnav: [
        { name: 'Home', url: '/' },
        { name: 'About', url: '/about' },
        { name: 'Services', url: '/services' },
        { name: 'Parts', url: '/parts' },
        { name: 'Fleet', url: '/fleet' },
        { name: 'Contact', url: '/contact' }
      ],
      footer: []
    },
    promotional: [{ slides: [] }],
    _dealership_id: undefined,
    isLoading: false,
    error: null,
    isInitialized: false
  }),

  getters: {
    siteName: (state) => state.name || 'My Nuxt App',
    siteDescription: (state) => state.description || 'Welcome to our website',
    mainNavLinks: (state) => state.sitelinks.mainnav || [],
    footerLinks: (state) => state.sitelinks.footer || [],
    salesHours: (state) => state.departments.sales.trading,
    serviceHours: (state) => state.departments.service.trading,
    partsHours: (state) => state.departments.parts.trading,
    allTeamMembers: (state) => state.team_members,
    teamMembersByDepartment: (state) => (department: string) => {
      return state.team_members.filter(member => member.department === department)
    },
    promotionalSlides: (state) => {
      if (state.promotional && Array.isArray(state.promotional) && state.promotional.length > 0 && state.promotional[0]) {
        if (state.promotional[0].slides && Array.isArray(state.promotional[0].slides)) {
          return state.promotional[0].slides
        } else {
          return []
        }
      }
      return []
    },
    isReady: (state) => !!state.name && !state.isLoading && state.isInitialized
  },
  
  actions: {
    async serverInit() {
      // Only run on server-side
      if (process.client) return
      
      try {
        const { data } = await useFetch('/api/site-config', {
          key: 'site-config-ssr',
          query: { _refresh: Date.now() } // Add cache-busting for fresh data
        })
        
        if (data.value) {
          this.$patch(data.value)
          this.isInitialized = true
        }
      } catch (error) {
        console.error('Failed to initialize site config:', error)
      }
    },

    async initialize() {
      // Client-side initialization just checks if data exists
      if (this.isInitialized) return
      
      // Try to fetch data from API if no data was hydrated
      if (!this.name || !this.departments.sales.phone) {
        console.log('[SiteStore] No data hydrated, fetching from API...')
        try {
          const data = await $fetch('/api/site-config', {
            query: { _refresh: Date.now() } // Add cache-busting for fresh data
          })
          if (data) {
            console.log('[SiteStore] Fetched site config:', data)
            this.$patch(data)
          }
        } catch (error) {
          console.error('[SiteStore] Failed to fetch site config:', error)
          // Set fallback values
          this.$patch({
            name: 'App',
            description: 'Welcome to our website',
            logo: '/logo.png',
            phone: '(03) 9999 9999',
            departments: {
              sales: {
                id: '1',
                phone: '(03) 9999 9999',
                address: 'Address not available',
                map_embed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.835434509374!2d144.9631!3d-37.8136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad65d4c2b349649%3A0xb6899234e561db11!2sMelbourne%20VIC%2C%20Australia!5e0!3m2!1sen!2sau!4v1640995200000!5m2!1sen!2sau',
                map_directions: 'https://www.google.com/maps/dir//Melbourne+VIC',
                trading: {
                  notice: [],
                  monday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  tuesday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  wednesday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  thursday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  friday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  saturday: [{ open: '9:00 am', close: '4:00 pm', current: { value: 'open', label: 'Open' } }],
                  sunday: [{ open: '', close: '', current: { value: 'closed', label: 'Closed' } }]
                }
              },
              used_sales: {
                id: '2',
                phone: '(03) 9999 9999',
                address: 'Address not available',
                map_embed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.835434509374!2d144.9631!3d-37.8136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad65d4c2b349649%3A0xb6899234e561db11!2sMelbourne%20VIC%2C%20Australia!5e0!3m2!1sen!2sau!4v1640995200000!5m2!1sen!2sau',
                map_directions: 'https://www.google.com/maps/dir//Melbourne+VIC',
                trading: {
                  notice: [],
                  monday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  tuesday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  wednesday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  thursday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  friday: [{ open: '8:00 am', close: '5:30 pm', current: { value: 'open', label: 'Open' } }],
                  saturday: [{ open: '9:00 am', close: '4:00 pm', current: { value: 'open', label: 'Open' } }],
                  sunday: [{ open: '', close: '', current: { value: 'closed', label: 'Closed' } }]
                }
              },
              service: {
                id: '3',
                phone: '(03) 9999 9999',
                address: 'Address not available',
                map_embed: '',
                map_directions: '',
                trading: { notice: [] }
              },
              parts: {
                id: '4',
                phone: '(03) 9999 9999',
                address: 'Address not available',
                map_embed: '',
                map_directions: '',
                trading: { notice: [] }
              }
            },
            sitelinks: {
              mainnav: [
                { name: 'Home', url: '/' },
                { name: 'About', url: '/about' },
                { name: 'Services', url: '/services' },
                { name: 'Parts', url: '/parts' },
                { name: 'Fleet', url: '/fleet' },
                { name: 'Contact', url: '/contact' }
              ],
              footer: []
            }
          })
        }
      }
      
      this.isInitialized = true
    }
  }
})
